// Pure baserunner animation helpers — no React/DOM dependencies, fully unit-testable.

export const BASE_ORDER = ["first", "second", "third"];

export function basesEqual(a, b) {
  return BASE_ORDER.every(k => (a?.[k]?.id || null) === (b?.[k]?.id || null));
}

/**
 * Given old and new base states (each a { first, second, third } object where values
 * are runner objects with an `id` field), produce an array of intermediate "frames"
 * that animate runners hop-by-hop through the bases.
 *
 * runsScored — how many runners actually crossed home plate (vs. being put out).
 * The `runsScored` closest-to-home disappearing runners are routed to pos=4 (scored);
 * the rest stay at their last base (out).
 *
 * opts.isDPPlay — when true, force-out victims advance one base forward before
 *   disappearing (shows them running to where they were tagged out).
 * opts.injectedBatter — a runner object for the batter when they aren't in either
 *   bases snapshot (HR: trot all 4 bases; DP batter out: trot to 1st only).
 * opts.injectedBatterDest — destination pos for the injected batter (4 = HR, 1 = out at 1st).
 */
export function computeBaseSteps(oldBases, newBases, runsScored = 0, opts = {}) {
  const { isDPPlay = false, injectedBatter = null, injectedBatterDest = null } = opts;
  if (!oldBases || !newBases) return [];
  const oldRunners = new Map();
  for (const k of BASE_ORDER) if (oldBases[k]?.id) oldRunners.set(oldBases[k].id, { ...oldBases[k], from: k });
  const newRunners = new Map();
  for (const k of BASE_ORDER) if (newBases[k]?.id) newRunners.set(newBases[k].id, { ...newBases[k], to: k });

  // Distinguish runners who scored vs were put out.
  // Runners closer to home plate score first (highest fromIdx).
  const disappearing = [];
  for (const [id, oldRunner] of oldRunners) {
    if (!newRunners.has(id)) {
      disappearing.push({ id, fromIdx: BASE_ORDER.indexOf(oldRunner.from) + 1 });
    }
  }
  disappearing.sort((a, b) => b.fromIdx - a.fromIdx);
  const scoringIds = new Set(disappearing.slice(0, runsScored).map(r => r.id));

  const paths = []; // { id, runner, from: 0-3, to: 1-4 (4 = scored) }
  for (const [id, oldRunner] of oldRunners) {
    const newSpot = newRunners.get(id);
    let to;
    if (newSpot) {
      to = BASE_ORDER.indexOf(newSpot.to) + 1;
    } else if (scoringIds.has(id)) {
      to = 4; // crossed home plate
    } else if (isDPPlay) {
      // Force-out: runner advanced one base ahead of their start (where they were tagged).
      // Cap at 3 so they never appear at home unless they actually scored (scoringIds path).
      to = Math.min(BASE_ORDER.indexOf(oldRunner.from) + 2, 3);
    } else {
      to = BASE_ORDER.indexOf(oldRunner.from) + 1; // put out — stay at current base
    }
    paths.push({
      id,
      runner: oldRunner,
      from: BASE_ORDER.indexOf(oldRunner.from) + 1,
      to,
    });
  }
  // Batter (in new but not in old) — entered from home plate (position 0).
  for (const [id, newRunner] of newRunners) {
    if (!oldRunners.has(id)) {
      paths.push({
        id,
        runner: newRunner,
        from: 0,
        to: BASE_ORDER.indexOf(newRunner.to) + 1,
      });
    }
  }
  // Injected batter: not in either bases snapshot (HR trot or DP batter out at 1st).
  if (injectedBatter?.id && injectedBatterDest != null
      && !oldRunners.has(injectedBatter.id)
      && !newRunners.has(injectedBatter.id)) {
    paths.push({
      id: injectedBatter.id,
      runner: injectedBatter,
      from: 0,
      to: injectedBatterDest,
    });
  }

  if (paths.length === 0) return [];
  const maxHops = Math.max(0, ...paths.map(p => p.to - p.from));
  if (maxHops <= 0) return [];

  // Stagger lead-first: the runner closest to home moves first.
  const orderedPaths = [...paths].sort((a, b) => b.from - a.from);
  const STAGGER_FRAMES = 1;
  const lastEndFrame = Math.max(
    ...orderedPaths.map((p, idx) => idx * STAGGER_FRAMES + (p.to - p.from))
  );
  if (lastEndFrame <= 0) return [];

  const steps = [];
  for (let frame = 1; frame <= lastEndFrame; frame++) {
    const stepBases = { first: null, second: null, third: null, home: null };
    orderedPaths.forEach((path, idx) => {
      const startOffset = idx * STAGGER_FRAMES;
      const hopsCompleted = Math.max(0, frame - startOffset);
      const pos = Math.min(path.from + hopsCompleted, path.to);
      if (pos >= 1 && pos <= 3) {
        stepBases[BASE_ORDER[pos - 1]] = path.runner;
      } else if (pos === 4) {
        stepBases.home = path.runner;
      }
    });
    steps.push(stepBases);
  }
  return steps;
}
